
// src/components/MetricsVisualisations.js
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../UserContext';
import { isVisualisationLocked } from '../data/tierConfig';
import { getMetricColor, buildLegendPayload } from '../utils/metricsColors';
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  CartesianGrid,
  ResponsiveContainer,
  ReferenceLine
} from 'recharts';
import html2canvas from 'html2canvas';

// ---- Compact currency formatting (auto scale) ----
export function detectCurrencyScale(maxAbs) {
  if (!Number.isFinite(maxAbs) || maxAbs === 0) return { divisor: 1, suffix: '', decimals: 0 };
  if (maxAbs >= 1e12) return { divisor: 1e12, suffix: 'T', decimals: 2 };
  if (maxAbs >= 1e11) return { divisor: 1e9,  suffix: 'B', decimals: 0 };
  if (maxAbs >= 1e9)  return { divisor: 1e9,  suffix: 'B', decimals: 1 };
  if (maxAbs >= 1e8)  return { divisor: 1e6,  suffix: 'M', decimals: 0 };
  if (maxAbs >= 1e6)  return { divisor: 1e6,  suffix: 'M', decimals: 1 };
  return { divisor: 1, suffix: '', decimals: 0 };
}

export function getCurrencySymbol(currency = 'USD') {
  return (
    new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
      currencyDisplay: 'narrowSymbol',
      maximumFractionDigits: 0,
    })
      .formatToParts(0)
      .find((p) => p.type === 'currency')?.value ?? '$'
  );
}

export function formatCurrencyFull(v, currency = 'USD') {
  const n = Number(v);
  if (!Number.isFinite(n)) return '';
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency,
    currencyDisplay: 'narrowSymbol',
    maximumFractionDigits: 0,
  }).format(n);
}

export function formatCurrencyWithScale(v, scale, currency = 'USD') {
  const n = Number(v);
  if (!Number.isFinite(n)) return '';

  const { divisor = 1, suffix = '', decimals = 0 } = scale || {};
  const symbol = getCurrencySymbol(currency);

  const prettySuffix =
    suffix === 'B' ? 'Bn' : suffix === 'M' ? 'Mn' : suffix === 'T' ? 'Tn' : '';

  if (divisor === 1 && suffix === '') return formatCurrencyFull(n, currency);

  const scaled = n / divisor;
  const num = new Intl.NumberFormat(undefined, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(scaled);

  return `${symbol}${num}${prettySuffix}`;
}

export function makeCompactCurrencyTickFormatter(scale, currency = 'USD') {
  return (value) => formatCurrencyWithScale(value, scale, currency);
}

// Support string or object selectedCompany
function pickCompanyBasics(selectedCompany) {
  if (!selectedCompany) return { companyName: '', ticker: '' };

  if (typeof selectedCompany === 'string') {
    return { companyName: '', ticker: selectedCompany.trim().toUpperCase() };
  }

  const name =
    selectedCompany.companyName ??
    selectedCompany.name ??
    selectedCompany.label ??
    selectedCompany.fullName ??
    '';

  const ticker =
    selectedCompany.symbol ??
    selectedCompany.ticker ??
    selectedCompany.tickerSymbol ??
    selectedCompany.value ??
    selectedCompany.code ??
    selectedCompany.id ??
    '';

  return {
    companyName: String(name).trim(),
    ticker: String(ticker).trim().toUpperCase(),
  };
}

// decode any accidental '&amp;' sequences for display
const decodeHtml = (s) => (s ? s.replaceAll('&amp;', '&').replaceAll('&amp;amp;', '&') : s);

const metricGroups = [
  'Returns on Capital',
  'Profitability Cascade',
  'Growth Trajectory Pulse',
  'FCF vs Net Income',
  'Debt & Coverage Profile',
  'Liquidity & Solvency',
  'Efficiency',
  'Equity & Book Value',
  'CFO Strength',
  'Cash Flow Allocation'
];

function toQuarterLabel(dateStr) {
  try {
    const [y, m] = dateStr.split('-').map(Number);
    const q = Math.floor((m - 1) / 3) + 1;
    return `${y}-Q${q}`;
  } catch {
    return dateStr;
  }
}

function normalizeRatio(v) {
  if (v === null || v === undefined) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function normalizeNumber(v) {
  if (v === null || v === undefined) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

const MetricsVisualisations = ({
  selectedCompany,
  historicalSeries = [],
  visualQuarters = 6,
  setVisualQuarters,
}) => {
  const chartRef = useRef(null);
  const [selectedGroup, setSelectedGroup] = useState(metricGroups[0]);
  const { userPlanTier } = useUser();
  const navigate = useNavigate();

  // Toggle how DPO is drawn:
  // true → DPO as a negative bar in the same stack
  const useNegativeDPO = true;

  useEffect(() => {
    console.log(
      '[MetricsVisualisations] historicalSeries isArray:',
      Array.isArray(historicalSeries),
      'length:',
      historicalSeries?.length
    );
  }, [historicalSeries]);

  const sliced = useMemo(() => {
    if (!Array.isArray(historicalSeries)) return [];
    const chronological = historicalSeries;
    return chronological.slice(Math.max(chronological.length - visualQuarters, 0));
  }, [historicalSeries, visualQuarters]);

  // Days formatting
  const daysFormatter = (v) =>
    v == null || Number.isNaN(Number(v)) ? '' : `${Number(v).toFixed(0)}d`;

  const ratioFormatter = (value) =>
    value == null ? '' : new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 }).format(value);

  const normalizedData = useMemo(() => {
    const rows = (sliced ?? []).map((row) => {
      const quarter = row.quarter || toQuarterLabel(row.date);

      const dpo = normalizeNumber(row.dpoDays);
      const dpoDaysVis = useNegativeDPO && dpo != null ? -dpo : dpo;

      return {
        ...row,
        quarter,

        revenue: normalizeNumber(row.revenue),
        grossProfit: normalizeNumber(row.grossProfit),
        grossProfitRatioPct: normalizeRatio(
          row.grossProfitRatioPct ?? row.grossMarginPct ?? row.grossProfitRatio
        ),

        operatingExpenses: normalizeNumber(row.operatingExpenses),
        operatingIncome: normalizeNumber(row.operatingIncome),
        operatingIncomeRatioPct: normalizeRatio(
          row.operatingIncomeRatioPct ?? row.operatingIncomeMarginPct ?? row.operatingIncomeRatio
        ),

        ebitda: normalizeNumber(row.ebitda),
        incomeBeforeTax: normalizeNumber(row.incomeBeforeTax),
        ebitdaMarginPct: normalizeRatio(row.ebitdaMarginPct ?? row.ebitdaMarginRatioPct ?? row.ebitdaMargin),
        incomeBeforeTaxRatioPct: normalizeRatio(
          row.incomeBeforeTaxRatioPct ?? row.incomeBeforeTaxMarginPct ?? row.preTaxMarginPct
        ),

        revenueGrowth: normalizeRatio(row.revenueGrowth),
        operatingIncomeGrowth: normalizeRatio(row.operatingIncomeGrowth),
        netIncomeGrowth: normalizeRatio(row.netIncomeGrowth),

        netIncomeRatio: normalizeRatio(row.netIncomeRatio),

        currentRatio: normalizeNumber(row.currentRatio),
        quickRatio: normalizeNumber(row.quickRatio),
        debtToEquity: normalizeNumber(row.debtToEquity),
        interestCoverage: normalizeNumber(row.interestCoverage),

        assetTurnover: normalizeNumber(row.assetTurnover),
        dsoDays: normalizeNumber(row.dsoDays),
        dioDays: normalizeNumber(row.dioDays),
        dpoDays: dpo,
        dpoDaysVis, // ✅ now exists
        cccDays: normalizeNumber(row.cccDays),

        totalAssets: normalizeNumber(row.totalAssets),
        totalCurrentAssets: normalizeNumber(row.totalCurrentAssets),
        nonCurrentAssets: normalizeNumber(row.nonCurrentAssets),

        totalLiabilities: normalizeNumber(row.totalLiabilities),
        totalCurrentLiabilities: normalizeNumber(row.totalCurrentLiabilities),
        nonCurrentLiabilities: normalizeNumber(row.nonCurrentLiabilities),

        freeCashFlow: normalizeNumber(row.freeCashFlow ?? row.fcf),
        operatingCashflow: normalizeNumber(row.operatingCashflow ?? row.operatingCashFlow),
        fcfMarginPct: normalizeRatio(row.fcfMarginPct ?? row.freeCashFlowMarginPct ?? row.fcfMargin),
        opCashFlowMarginPct: normalizeRatio(row.opCashFlowMarginPct ?? row.operatingCashFlowMarginPct),

        capitalExpenditure: normalizeNumber(row.capitalExpenditure ?? row.capex),
        capexCoverage: (() => {
          if (row.capexCoverage) return normalizeNumber(row.capexCoverage);
          const opCf = normalizeNumber(row.operatingCashflow ?? row.operatingCashFlow);
          const capex = normalizeNumber(row.capitalExpenditure ?? row.capex);
          return capex && capex !== 0 ? (opCf / Math.abs(capex)) * 100 : 0;
        })(),

        // Returns on Capital
        roePct: normalizeRatio(row.roePct),
        roaPct: normalizeRatio(row.roaPct),
        roicPct: normalizeRatio(row.roicPct),

        // FCF vs Net Income
        netIncome: normalizeNumber(row.netIncome),

        // Equity & Book Value
        retainedEarnings: normalizeNumber(row.retainedEarnings),

        // Debt & Coverage
        totalDebt: normalizeNumber(row.totalDebt),
        netDebt: normalizeNumber(row.netDebt),
      };
    });

    return rows;
  }, [sliced, useNegativeDPO]);

  // Revenue scaling
  const maxRevenueAbs = useMemo(() => {
    return Math.max(0, ...((normalizedData || []).map((d) => Math.abs(Number(d.revenue) || 0))));
  }, [normalizedData]);

  const revenueScale = useMemo(() => detectCurrencyScale(maxRevenueAbs), [maxRevenueAbs]);
  const revenueTickFormatter = useMemo(
    () => makeCompactCurrencyTickFormatter(revenueScale, 'USD'),
    [revenueScale]
  );

  const { companyName, ticker: safeTicker } = useMemo(
    () => pickCompanyBasics(selectedCompany),
    [selectedCompany]
  );

  const baseGroupTitle = decodeHtml(selectedGroup || 'Metrics');
  const chartTitle = useMemo(() => {
    return safeTicker ? `${baseGroupTitle} | ${safeTicker}` : baseGroupTitle;
  }, [baseGroupTitle, safeTicker]);

  const toSafe = (s) => (s || '').toString().replace(/[^A-Za-z0-9._-]/g, '-').replace(/-+/g, '-');

  const handleDownloadPNG = async () => {
    if (!chartRef.current) return;
    const canvas = await html2canvas(chartRef.current);
    const link = document.createElement('a');
    link.href = canvas.toDataURL('image/png');
    const fileId = toSafe(safeTicker || companyName || 'company');
    const groupId = toSafe(baseGroupTitle);
    link.download = `${fileId}-${groupId}-chart.png`;
    link.click();
  };

  const handleDownloadCSV = () => {
    if (!sliced?.length) return;
    const headers = Object.keys(sliced[0]);
    const rows = sliced.map((row) => headers.map((h) => (row[h] ?? '')).join(','));
    const csvContent = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    const fileId = toSafe(safeTicker || companyName || 'company');
    const groupId = toSafe(baseGroupTitle);
    link.download = `${fileId}-${groupId}-data.csv`;
    link.click();
  };

  const renderChart = () => {
    if (!normalizedData?.length) {
      return <p className="text-gray-600">No data available for the selected company/period.</p>;
    }

  switch (selectedGroup) {
    case 'Returns on Capital': {
      return (
        <ComposedChart data={normalizedData}>
          <CartesianGrid stroke="#f5f5f5" />
          <XAxis dataKey="quarter" />
          <YAxis
            yAxisId="left"
            tickFormatter={(v) => `${v.toFixed(1)}%`}
            domain={['auto', 'auto']}
          />
          <Tooltip
            itemSorter={(item) => {
              const order = { roePct: 1, roicPct: 2, roaPct: 3 };
              const key = item?.dataKey ?? item?.name;
              return order[key] ?? 99;
            }}
            formatter={(value, name, entry) => {
              const key = entry?.dataKey ?? name;
              const labelMap = {
                roePct: 'Return on Equity (ROE)',
                roaPct: 'Return on Assets (ROA)',
                roicPct: 'Return on Invested Capital (ROIC)',
              };
              const label = labelMap[key] ?? name;
              return [`${Number(value).toFixed(2)}%`, label];
            }}
          />
          <Legend
            payload={buildLegendPayload([
              { id: 'roePct', label: 'ROE', type: 'line' },
              { id: 'roicPct', label: 'ROIC', type: 'line' },
              { id: 'roaPct', label: 'ROA', type: 'line' },
            ])}
          />
          <ReferenceLine yAxisId="left" y={0} stroke="#bbb" strokeDasharray="3 3" />
          <Line yAxisId="left" type="monotone" dataKey="roePct" name="ROE" stroke={getMetricColor('roePct')} dot={{ r: 3 }} strokeWidth={2} />
          <Line yAxisId="left" type="monotone" dataKey="roicPct" name="ROIC" stroke={getMetricColor('roicPct')} dot={{ r: 3 }} strokeWidth={2} />
          <Line yAxisId="left" type="monotone" dataKey="roaPct" name="ROA" stroke={getMetricColor('roaPct')} dot={{ r: 3 }} strokeWidth={2} />
        </ComposedChart>
      );
    }

    case 'Growth Trajectory Pulse': {
      return (
        <ComposedChart data={normalizedData}>
          <CartesianGrid stroke="#f5f5f5" />
          <XAxis dataKey="quarter" />
          <YAxis
            yAxisId="left"
            orientation="left"
            tickFormatter={(v) => `${v.toFixed(0)}%`}
            domain={[0, (dataMin, dataMax) => Math.min(1, (dataMax || 0) + 0.05)]}
          />
          <Tooltip
            itemSorter={(item) => {
              const order = { revenueGrowth: 1, operatingIncomeGrowth: 2, netIncomeGrowth: 3 };
              const key = item?.dataKey ?? item?.name;
              return order[key] ?? 99;
            }}
            formatter={(value, name, entry) => {
              const key = entry?.dataKey ?? name;
              const labelMap = {
                revenueGrowth: 'Revenue Growth',
                operatingIncomeGrowth: 'Operating Income Growth',
                netIncomeGrowth: 'Net Income Growth',
              };
              const label = labelMap[key] ?? name;
              if (key === 'revenueGrowth' || key === 'operatingIncomeGrowth' || key === 'netIncomeGrowth') {
                return [`${Number(value).toFixed(2)}%`, label];
              }
              return [value, label];
            }}
          />
          <Legend
            payload={buildLegendPayload([
              { id: 'revenueGrowth', label: 'Revenue Growth' },
              { id: 'netIncomeGrowth', label: 'Net Income Growth' },
              { id: 'operatingIncomeGrowth', label: 'Operating Income Growth' },
            ])}
          />
          <Line yAxisId="left" dataKey="revenueGrowth" name="Revenue Growth" stroke={getMetricColor('revenueGrowth')} dot={false} strokeWidth={2} />
          <Line yAxisId="left" dataKey="netIncomeGrowth" name="Net Income Growth" stroke={getMetricColor('netIncomeGrowth')} dot={false} strokeWidth={2} />
          <Line yAxisId="left" type="monotone" dataKey="operatingIncomeGrowth" name="Operating Income Growth" stroke={getMetricColor('operatingIncomeGrowth')} dot={false} strokeWidth={2} />
        </ComposedChart>
      );
    }

    case 'FCF vs Net Income': {
      return (
        <ComposedChart data={normalizedData}>
          <CartesianGrid stroke="#f5f5f5" />
          <XAxis dataKey="quarter" />
          <YAxis yAxisId="left" tickFormatter={revenueTickFormatter} />
          <Tooltip
            itemSorter={(item) => {
              const order = { freeCashFlow: 1, netIncome: 2 };
              const key = item?.dataKey ?? item?.name;
              return order[key] ?? 99;
            }}
            formatter={(value, name, entry) => {
              const key = entry?.dataKey ?? name;
              const labelMap = {
                freeCashFlow: 'Free Cash Flow',
                netIncome: 'Net Income',
              };
              const label = labelMap[key] ?? name;
              return [formatCurrencyWithScale(Number(value), revenueScale, 'USD'), label];
            }}
          />
          <Legend
            payload={buildLegendPayload([
              { id: 'freeCashFlow', label: 'Free Cash Flow' },
              { id: 'netIncome', label: 'Net Income' },
            ])}
          />
          <ReferenceLine yAxisId="left" y={0} stroke="#bbb" strokeDasharray="3 3" />
          <Bar yAxisId="left" dataKey="freeCashFlow" name="Free Cash Flow" barSize={22} fill={getMetricColor('freeCashFlow')} />
          <Bar yAxisId="left" dataKey="netIncome" name="Net Income" barSize={22} fill={getMetricColor('netIncome')} />
        </ComposedChart>
      );
    }

    case 'Debt & Coverage Profile': {
      return (
        <ComposedChart data={normalizedData}>
          <CartesianGrid stroke="#f5f5f5" />
          <XAxis dataKey="quarter" />
          <YAxis yAxisId="left" tickFormatter={revenueTickFormatter} />
          <YAxis
            yAxisId="right"
            orientation="right"
            tickFormatter={(v) => `${v.toFixed(1)}×`}
            domain={[0, 'auto']}
          />
          <Tooltip
            itemSorter={(item) => {
              const order = { totalDebt: 1, netDebt: 2, interestCoverage: 3 };
              const key = item?.dataKey ?? item?.name;
              return order[key] ?? 99;
            }}
            formatter={(value, name, entry) => {
              const key = entry?.dataKey ?? name;
              const labelMap = {
                totalDebt: 'Total Debt',
                netDebt: 'Net Debt',
                interestCoverage: 'Interest Coverage',
              };
              const label = labelMap[key] ?? name;
              if (key === 'interestCoverage') {
                return [`${Number(value).toFixed(2)}×`, label];
              }
              return [formatCurrencyWithScale(Number(value), revenueScale, 'USD'), label];
            }}
          />
          <Legend
            payload={buildLegendPayload([
              { id: 'totalDebt', label: 'Total Debt' },
              { id: 'netDebt', label: 'Net Debt' },
              { id: 'interestCoverage', label: 'Interest Coverage', type: 'line' },
            ])}
          />
          <Bar yAxisId="left" dataKey="totalDebt" name="Total Debt" barSize={22} fill={getMetricColor('totalDebt')} />
          <Bar yAxisId="left" dataKey="netDebt" name="Net Debt" barSize={22} fill={getMetricColor('netDebt')} />
          <Line yAxisId="right" type="monotone" dataKey="interestCoverage" name="Interest Coverage" stroke={getMetricColor('interestCoverage')} dot={{ r: 3 }} strokeWidth={2} />
        </ComposedChart>
      );
    }

    case 'Profitability Cascade': {
      return (
        <ComposedChart data={normalizedData}>
          <CartesianGrid stroke="#f5f5f5" />
          <XAxis dataKey="quarter" />
          <YAxis yAxisId="left" tickFormatter={revenueTickFormatter} />
          <YAxis
            yAxisId="right"
            orientation="right"
            tickFormatter={(v) => `${v.toFixed(0)}%`}
            domain={[0, (dataMin, dataMax) => Math.min(1, (dataMax || 0) + 0.05)]}
          />
          <Tooltip
            itemSorter={(item) => {
              const order = { revenue: 1, grossProfitRatioPct: 2, operatingIncomeRatioPct: 3, netIncomeRatio: 4 };
              const key = item?.dataKey ?? item?.name;
              return order[key] ?? 99;
            }}
            formatter={(value, name, entry) => {
              const key = entry?.dataKey ?? name;
              const labelMap = {
                revenue: 'Total Revenue',
                grossProfitRatioPct: 'Gross Profit Ratio',
                operatingIncomeRatioPct: 'Operating Income Ratio',
                netIncomeRatio: 'Net Income Ratio',
              };
              const label = labelMap[key] ?? name;

              if (key === 'revenue') {
                return [formatCurrencyWithScale(Number(value), revenueScale, 'USD'), label];
              }
              if (key === 'grossProfitRatioPct' || key === 'operatingIncomeRatioPct' || key === 'netIncomeRatio') {
                return [`${Number(value).toFixed(2)}%`, label];
              }
              return [value, label];
            }}
          />
          <Legend
            payload={buildLegendPayload([
              { id: 'revenue', label: 'Total Revenue' },
              { id: 'grossProfitRatioPct', label: 'Gross Profit Ratio', type: 'line' },
              { id: 'netIncomeRatio', label: 'Net Income Ratio', type: 'line' },
              { id: 'operatingIncomeRatioPct', label: 'Operating Income Ratio', type: 'line' },
            ])}
          />
          <Bar yAxisId="left" dataKey="revenue" name="Total Revenue" barSize={22} fill={getMetricColor('revenue')} />
          <Line yAxisId="right" type="monotone" dataKey="grossProfitRatioPct" name="Gross Profit Ratio" stroke={getMetricColor('grossProfitRatioPct')} dot={false} strokeWidth={2} />
          <Line yAxisId="right" type="monotone" dataKey="netIncomeRatio" name="Net Income Ratio" stroke={getMetricColor('netIncomeRatio')} dot={false} strokeWidth={2} />
          <Line yAxisId="right" type="monotone" dataKey="operatingIncomeRatioPct" name="Operating Income Ratio" stroke={getMetricColor('operatingIncomeRatioPct')} dot={false} strokeWidth={2} />
        </ComposedChart>
      );
    }

    case 'Liquidity & Solvency': {
      return (
        <ComposedChart data={normalizedData}>
          <CartesianGrid stroke="#f5f5f5" />
          <XAxis dataKey="quarter" />
          <YAxis yAxisId="left" tickFormatter={ratioFormatter} domain={[0, (dataMin, dataMax) => Math.min(1, (dataMax || 0) + 0.05)]} />
          <Tooltip
            itemSorter={(item) => {
              const order = { quickRatio: 1, currentRatio: 2, debtToEquity: 3 };
              const key = item?.dataKey ?? item?.name;
              return order[key] ?? 99;
            }}
            formatter={(value, name, entry) => {
              const key = entry?.dataKey ?? name;
              const labelMap = {
                quickRatio: 'Quick Ratio',
                currentRatio: 'Current Ratio',
                debtToEquity: 'Debt to Equity Ratio',
              };
              const label = labelMap[key] ?? name;
              if (key === 'quickRatio' || key === 'currentRatio' || key === 'debtToEquity') {
                return [ratioFormatter(Number(value), revenueScale, 'USD'), label];
              }
              return [value, label];
            }}
          />
          <Legend />
          <Line yAxisId="left" dataKey="currentRatio" name="Current Ratio" stroke={getMetricColor('currentRatio')} dot={false} strokeWidth={2} />
          <Line yAxisId="left" type="monotone" dataKey="quickRatio" name="Quick Ratio" stroke={getMetricColor('quickRatio')} dot={false} strokeWidth={2} />
          <Line yAxisId="left" type="monotone" dataKey="debtToEquity" name="Debt to Equity Ratio" stroke={getMetricColor('debtToEquity')} dot={false} strokeWidth={2} />
        </ComposedChart>
      );
    }

    case 'Efficiency': {
      return (
        <ResponsiveContainer width="100%" height={360}>
          <ComposedChart data={normalizedData} margin={{ top: 8, right: 24, left: 8, bottom: 8 }}>
            <CartesianGrid stroke="#f5f5f5" />
            <XAxis dataKey="quarter" />

            {/* LEFT Y-AXIS: Days-based metrics (CCC, DSO, DIO, DPO) */}
            <YAxis
              yAxisId="left"
              tickFormatter={daysFormatter}
              // Allow negatives if using negative DPO bars
              domain={useNegativeDPO ? ['dataMin - 10', 'dataMax + 10'] : [0, 'dataMax + 10']}
            />

            {/* RIGHT Y-AXIS: Asset turnover ratio */}
            <YAxis
              yAxisId="right"
              orientation="right"
              tickFormatter={ratioFormatter}
              domain={[0, (dataMin, dataMax) => Math.max(1.5, (dataMax || 0) + 0.2)]}
            />

            <Tooltip
              formatter={(value, name, entry) => {
                const key = entry?.dataKey ?? name;
                const labelMap = {
                  assetTurnover: 'Asset Turnover',
                  cccDays: 'Cash Conversion Cycle (Days)',
                  dsoDays: 'Days Sales Outstanding (Days)',
                  dioDays: 'Days Inventory Outstanding (Days)',
                  dpoDays: 'Days Payables Outstanding (Days)',
                  dpoDaysVis: 'Days Payables Outstanding (Days)', // same label for negative-bar mode
            };
            const label = labelMap[key] ?? key;

            // Decide units by metric type
            if (key === 'assetTurnover') return [ratioFormatter(value), label];
            return [daysFormatter(useNegativeDPO && key === 'dpoDaysVis' ? -value : value), label];
          }}
          itemSorter={(item) => {
            // ensure a sensible display order in the tooltip
            const order = {
              cccDays: 1,
              dsoDays: 2,
              dioDays: 3,
              dpoDays: 4,
              dpoDaysVis: 4,
              assetTurnover: 5,
            };
            return order[item?.dataKey ?? item?.name] ?? 99;
          }}
        />

        <Legend />

        {/* --- (a) Stacked Bars for DSO + DIO --- */}
        <Bar
          yAxisId="left"
          dataKey="dsoDays"
          name="DSO (Days)"
          stackId="days"
          barSize={22} 
          fill={getMetricColor('dsoDays')}
          radius={[3, 3, 0, 0]}
        />
        <Bar
          yAxisId="left"
          dataKey="dioDays"
          name="DIO (Days)"
          stackId="days"
          barSize={22} 
          fill={getMetricColor('dioDays')}
          radius={[3, 3, 0, 0]}
        />

        {/* --- (c) DPO handling --- */}
        {useNegativeDPO ? (
          <>
            {/* show DPO as negative bar in the same stack, so net bar height = CCC */}
            <Bar
              yAxisId="left"
              dataKey="dpoDaysVis"
              name="DPO (Days)"
              stackId="days"
              barSize={22} 
              fill={getMetricColor('dpoDays')}
            />
            {/* zero line helps users read negative DPO visually */}
            <ReferenceLine y={0} yAxisId="left" stroke="#bbb" />
          </>
        ) : (
          // Alternative: plot DPO as a dashed line (no sign flip)
          <Line
            yAxisId="left"
            type="monotone"
            dataKey="dpoDays"
            name="DPO (Days)"
            stroke={getMetricColor('dpoDays')}
            dot={false}
            strokeWidth={2}
            strokeDasharray="4 4"
          />
        )}

        {/* --- (b) CCC as a line --- */}
        <Line
          yAxisId="left"
          type="monotone"
          dataKey="cccDays"
          name="CCC (Days)"
          stroke={getMetricColor('cccDays')}
          dot={false}
          strokeWidth={3}
        />

        {/* Optional: keep Asset Turnover as a right-axis line for context */}
        <Line
          yAxisId="right"
          type="monotone"
          dataKey="assetTurnover"
          name="Asset Turnover (×)"
          stroke={getMetricColor('assetTurnover')}
          dot={false}
          strokeWidth={2}
        />
      </ComposedChart>
    </ResponsiveContainer>
  );
}

    case 'Equity & Book Value': {
      return (
        <ComposedChart data={normalizedData}>
          <CartesianGrid stroke="#f5f5f5" />
          <XAxis dataKey="quarter" />
          <YAxis yAxisId="left" tickFormatter={revenueTickFormatter} />
          <Tooltip
            itemSorter={(item) => {
              const order = { totalEquity: 1, retainedEarnings: 2, totalAssets: 3, totalLiabilities: 4 };
              const key = item?.dataKey ?? item?.name;
              return order[key] ?? 99;
            }}
            formatter={(value, name, entry) => {
              const key = entry?.dataKey ?? name;
              const labelMap = {
                totalEquity: "Stockholders' Equity",
                retainedEarnings: 'Retained Earnings',
                totalAssets: 'Total Assets',
                totalLiabilities: 'Total Liabilities',
              };
              const label = labelMap[key] ?? name;
              return [formatCurrencyWithScale(Number(value), revenueScale, 'USD'), label];
            }}
          />
          <Legend
            payload={buildLegendPayload([
              { id: 'totalEquity', label: "Stockholders' Equity" },
              { id: 'retainedEarnings', label: 'Retained Earnings' },
              { id: 'totalAssets', label: 'Total Assets', type: 'line' },
              { id: 'totalLiabilities', label: 'Total Liabilities', type: 'line' },
            ])}
          />
          <Bar yAxisId="left" dataKey="totalEquity" name="Stockholders' Equity" barSize={22} fill={getMetricColor('totalEquity')} />
          <Bar yAxisId="left" dataKey="retainedEarnings" name="Retained Earnings" barSize={22} fill={getMetricColor('retainedEarnings')} />
          <Line yAxisId="left" type="monotone" dataKey="totalAssets" name="Total Assets" stroke={getMetricColor('totalAssets')} dot={false} strokeWidth={2} />
          <Line yAxisId="left" type="monotone" dataKey="totalLiabilities" name="Total Liabilities" stroke={getMetricColor('totalLiabilities')} dot={false} strokeWidth={2} />
        </ComposedChart>
      );
    }

    case 'CFO Strength': {
      return (
        <ComposedChart data={normalizedData}>
          <CartesianGrid stroke="#f5f5f5" />
          <XAxis dataKey="quarter" />
          <YAxis yAxisId="left" tickFormatter={revenueTickFormatter} domain={[0, (dataMin, dataMax) => Math.min(1, (dataMax || 0) + 0.05)]} />
          <YAxis
            yAxisId="right"
            orientation="right"
            tickFormatter={(v) => `${v.toFixed(0)}%`}
            domain={[0, (dataMin, dataMax) => Math.min(1, (dataMax || 0) + 0.05)]}
          />
          <Tooltip
            itemSorter={(item) => {
              const order = { freeCashFlow: 1, operatingIncome: 2, fcfMarginPct: 3 };
              const key = item?.dataKey ?? item?.name;
              return order[key] ?? 99;
            }}
            formatter={(value, name, entry) => {
              const key = entry?.dataKey ?? name;
              const labelMap = {
                freeCashFlow: 'Free Cash Flow',
                operatingIncome: 'Operating Income',
                fcfMarginPct: 'Free Cash Flow Margin',
              };
              const label = labelMap[key] ?? name;

              if (key === 'freeCashFlow' || key === 'operatingIncome') {
                return [formatCurrencyWithScale(Number(value), revenueScale, 'USD'), label];
              }
              if (key === 'fcfMarginPct') {
                return [`${Number(value).toFixed(2)}%`, label];
              }
              return [value, label];
            }}
          />
          <Legend />
          <Bar yAxisId="left" dataKey="freeCashFlow" name="Free Cash Flow" barSize={22} fill={getMetricColor('freeCashFlow')} />
          <Bar yAxisId="left" dataKey="operatingIncome" name="Operating Cash Flow" barSize={22} fill={getMetricColor('operatingIncome')} />
          <Line yAxisId="right" type="monotone" dataKey="fcfMarginPct" name="FCF Margin (%)" stroke={getMetricColor('fcfMarginPct')} dot={false} strokeWidth={2} />
        </ComposedChart>
      );
    }

    case 'Cash Flow Allocation': {
      return (
        <ComposedChart data={normalizedData}>
          <CartesianGrid stroke="#f5f5f5" />
          <XAxis dataKey="quarter" />

          {/* Left axis: currency for bars */}
          <YAxis
            yAxisId="left"
            tickFormatter={revenueTickFormatter}
          />

          {/* Right axis: percentage for coverage line (0–100) */}
          <YAxis
            yAxisId="right"
            orientation="right"
            tickFormatter={(v) => `${v.toFixed(0)}%`}
            // Add a small headroom above max (at least 100%)
            domain={[0, (dataMin, dataMax) => Math.max(100, (dataMax || 0) + 10)]}
          />

          <Tooltip
            formatter={(value, name, entry) => {
              const key = entry?.dataKey ?? name;
              const labelMap = {
                operatingCashflow: 'Operating Cashflow',
                capitalExpenditure: 'Capital Expenditure',
                capexCoverage: 'CapEx Coverage',
              };
              const label = labelMap[key] ?? name;

              // Bars (currency)
              if (key === 'operatingCashflow' || key === 'capitalExpenditure') {
                return [formatCurrencyWithScale(Number(value), revenueScale, 'USD'), label];
              }

              // Line (percentage 0–100)
              if (key === 'capexCoverage') {
                return [`${Number(value).toFixed(1)}%`, label];
              }

              return [value, label];
            }}
          />
          <Legend />

          {/* Bars side-by-side (absolute values) */}
          <Bar
            yAxisId="left"
            dataKey="operatingCashflow"
            name="Operating Cashflow"
            barSize={22}
            fill={getMetricColor?.('operatingCashflow') ?? '#2CA58D'}
          />
          <Bar
            yAxisId="left"
            dataKey="capitalExpenditure"
            name="Capital Expenditure"
            barSize={22}
            fill={getMetricColor?.('capitalExpenditure') ?? '#F28E2B'}
          />

          {/* Coverage Line on the right Y axis (percentage) */}
          <Line
            yAxisId="right"
            type="monotone"
            dataKey="capexCoverage"     // <-- your backend field (0–100)
            name="CapEx Coverage"
            stroke={getMetricColor?.('capexCoverage') ?? '#4E79A7'}
            dot={false}
            strokeWidth={2}
            isAnimationActive={false}
          />

          {/* 100% reference line */}
          <ReferenceLine
            yAxisId="right"
            y={100}
            stroke="#9AA0A6"
            strokeDasharray="4 4"
            ifOverflow="extendDomain"
            label={{
              value: '100% Coverage',
              position: 'right',
              fill: '#6b7280',
              fontSize: 12
            }}
          />
        </ComposedChart>
      );
    }

      default:
        return <p className="text-gray-600">No chart available for the selected group.</p>;
    }
  };

  const chartEl = renderChart();
  const isLocked = isVisualisationLocked(userPlanTier, selectedGroup);

  // Determine upgrade label based on what tier would unlock this
  const upgradeTier = userPlanTier === 'free' ? 'Pro' : 'Premium';

  return (
    <div className="w-full min-w-0">
      <h2 className="text-xl font-bold mb-4">Metrics Visualisations</h2>

      {/* ✅ Mobile-safe controls: stack on mobile, wrap on larger screens */}
      <div className="flex flex-col sm:flex-row sm:flex-wrap gap-3 mb-4 w-full min-w-0">
        <select
          value={selectedGroup}
          onChange={(e) => setSelectedGroup(e.target.value)}
          className="border p-2 rounded w-full sm:w-auto"
        >
          {metricGroups.map((group) => {
            const locked = isVisualisationLocked(userPlanTier, group);
            return (
              <option key={group} value={group}>
                {locked ? `🔒 ${group}` : group}
              </option>
            );
          })}
        </select>

        <select
          value={visualQuarters}
          onChange={(e) => setVisualQuarters(Number(e.target.value))}
          className="border p-2 rounded w-full sm:w-auto"
        >
          <option value={4}>Last 4 Quarters</option>
          <option value={6}>Last 6 Quarters</option>
          <option value={8}>Last 8 Quarters</option>
        </select>

        {!isLocked && (
          <>
            <button
              onClick={handleDownloadPNG}
              className="bg-blue-500 text-white px-4 py-2 rounded w-full sm:w-auto"
            >
              Download PNG
            </button>

            <button
              onClick={handleDownloadCSV}
              className="bg-green-500 text-white px-4 py-2 rounded w-full sm:w-auto"
            >
              Download CSV
            </button>
          </>
        )}
      </div>

      {/* ✅ Chart wrapper: prevent any horizontal overflow */}
      <div
        ref={chartRef}
        className="bg-white p-4 rounded shadow w-full min-w-0 overflow-hidden"
      >
        <h3 className="text-base font-semibold mb-3 text-gray-800 text-center break-words">
          {isLocked ? selectedGroup : (chartTitle || ' ')}
        </h3>

        {isLocked ? (
          <div className="relative w-full h-[360px] sm:h-[400px]">
            {/* Blurred placeholder */}
            <div className="absolute inset-0 bg-gradient-to-br from-gray-100 to-gray-200 rounded-lg blur-sm" aria-hidden="true">
              <div className="flex items-end justify-around h-full px-8 pb-12 opacity-30">
                {[65, 45, 80, 55, 70, 40].map((h, i) => (
                  <div key={i} className="bg-gray-400 rounded-t" style={{ width: '12%', height: `${h}%` }} />
                ))}
              </div>
            </div>
            {/* Upgrade overlay */}
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-white/70 rounded-lg">
              <span className="text-3xl mb-3">🔒</span>
              <p className="text-sm font-semibold text-gray-700 mb-1">
                {selectedGroup} is a {upgradeTier} feature
              </p>
              <p className="text-xs text-gray-500 mb-3">
                Upgrade to unlock this visualisation and more
              </p>
              <button
                className="bg-brand-600 text-white text-sm font-semibold px-5 py-2 rounded-md hover:bg-brand-700 transition-colors"
                onClick={() => navigate('/subscribe')}
              >
                Upgrade to {upgradeTier}
              </button>
            </div>
          </div>
        ) : (
          <div className="w-full h-[360px] sm:h-[400px] min-w-0">
            <ResponsiveContainer width="100%" height="100%">
              {chartEl}
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </div>
  );
};

export default MetricsVisualisations;
